import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import useSWR from 'swr';
import { toast } from 'App';
import { UpdatePermissionGate } from 'components/PermissionGate';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { adminService } from 'views/services/adminService';
import { feesService } from 'views/services/feesService';

interface IFormValues {
  // Shown/edited as a percentage (0.2 = 0.2%); converted to a fraction on save.
  feePercent: number;
}

/**
 * Global swap fee (one rate for every pair — client decision 2026-08-06).
 * Applied to the out-amount at swap-order creation; in-flight orders keep the
 * rate they were quoted.
 */
export const SwapFeePanel = () => {
  const [saving, setSaving] = useState(false);
  const { data, isLoading, mutate } = useSWR('/api/admin/fees/swap', () =>
    feesService.getSwapFees()
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IFormValues>({ mode: 'onChange' });

  useEffect(() => {
    if (data) {
      reset({ feePercent: Number((data.feePct * 100).toFixed(4)) });
    }
  }, [data, reset]);

  const onSubmit = async (values: IFormValues) => {
    try {
      setSaving(true);
      const updated = await feesService.updateSwapFees({
        feePct: Number(values.feePercent) / 100,
      });
      await mutate(updated, { revalidate: false });
      toast({
        title: 'Swap fee updated',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      adminService.errorHandler(error);
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    'w-full rounded-lg border border-gray-300 p-2.5 text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white';

  return (
    <div className="max-w-xl">
      <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
        One rate for every swap pair, taken from the amount the user receives.
        Changes apply to new swaps immediately &mdash; no redeploy needed.
      </p>
      {isLoading ? (
        <span className="text-gray-500">Loading…</span>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div>
            <label className="mb-1 block text-sm font-medium text-navy-700 dark:text-white">
              Swap fee (%)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="20"
              className={inputCls}
              {...register('feePercent', {
                required: 'Required',
                min: { value: 0, message: 'Must be ≥ 0' },
                max: { value: 20, message: 'Must be ≤ 20' },
                valueAsNumber: true,
              })}
            />
            {errors.feePercent && (
              <p className="mt-1 text-xs text-red-500">
                {errors.feePercent.message}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-400">
              Percentage of the out-amount (e.g. 0.2 = 0.2%).
            </p>
          </div>

          <UpdatePermissionGate resource={PERMISSION_RESOURCES.FEES}>
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-brand-500 px-5 py-2.5 font-medium text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </UpdatePermissionGate>
        </form>
      )}
    </div>
  );
};
